/**
 * PrivacyCard — Profile privacy settings card on the Settings page.
 *
 * Renders two selectable option rows ("Private" / "Members") that drive the
 * master profile visibility (profileVisibility). When set to PRIVATE the
 * profile page shows a generic "private profile" message instead of any
 * content; a warning callout explains the side effects. Individual field
 * visibility settings elsewhere on the page are subordinate to this master.
 *
 * @route /profile/edit
 * @parent CommunityProfileSettingsPage
 */

import React from 'react'
import InfoCircled from '../../../../../Icon/icons/InfoCircled/index'
import { useTranslatedText } from '../../../../../StaticText/StaticTextContext'
import SettingsCard from './SettingsCard'
import PrivacyOptionRow from './PrivacyOptionRow'

const CLS = 'community-profile-settings'

export interface PrivacyCardProps {
  profileVisibility: string
  onProfileVisibilityChange: (value: string) => void
}

function PrivacyCard ({
  profileVisibility,
  onProfileVisibilityChange,
}: PrivacyCardProps) {
  const isStealthMode = profileVisibility === 'private'

  const privacyTitleText = useTranslatedText({ keys: ['privacyTitle'] })
  const privacySubtitleText = useTranslatedText({ keys: ['privacySubtitle'] })
  const privateProfileText = useTranslatedText({ keys: ['privateProfile'] })
  const privateProfileDescriptionText = useTranslatedText({ keys: ['privateProfileDescription'] })
  const membersOptionText = useTranslatedText({ keys: ['membersOption'] })
  const membersOptionDescriptionText = useTranslatedText({ keys: ['membersOptionDescription'] })
  const privateWarningText = useTranslatedText({ keys: ['privateWarningText'] })

  return (
    <SettingsCard
      title={privacyTitleText}
      subtitle={privacySubtitleText}
      bordered
    >
      <div
        className={`${CLS}__privacy-options`}
        role="radiogroup"
        aria-label={privacyTitleText}
      >
        <PrivacyOptionRow
          title={privateProfileText}
          description={privateProfileDescriptionText}
          selected={isStealthMode}
          onSelect={() => onProfileVisibilityChange('private')}
        />
        <PrivacyOptionRow
          title={membersOptionText}
          description={membersOptionDescriptionText}
          selected={!isStealthMode}
          onSelect={() => onProfileVisibilityChange('public')}
        />
      </div>

      <div className={`${CLS}__privacy-warning`}>
        <InfoCircled />
        <p className={`${CLS}__privacy-warning-text`}>
          {privateWarningText}
        </p>
      </div>
    </SettingsCard>
  )
}

export default PrivacyCard
